import { createPortal } from "react-dom";
import { useState, useCallback } from "react";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Briefcase,
  CheckSquare,
  FileText,
  HelpCircle,
  Mail,
  Palette,
  Star,
  X,
} from "lucide-react";
import { cn } from "@repo/utils";
import { Button } from "@repo/ui";
import type { VariablesData } from "../lib/types";

interface Slide {
  icon: React.ReactNode;
  title: string;
  content: (variablesData: VariablesData) => React.ReactNode;
}

const slides: Slide[] = [
  {
    icon: <Briefcase className="h-12 w-12 text-primary" />,
    title: "Bienvenue dans l'évaluation InBasket",
    content: () => (
      <div className="space-y-3 text-left">
        <p>
          InBasket est un outil de{" "}
          <strong>mise en situation professionnelle</strong> utilisé dans un
          objectif de développement personnel et de formation.
        </p>
        <p>
          Il s'agit d'un ensemble de mails qui retracent des problématiques
          managériales. Dans cette mise en situation, il sera observé votre{" "}
          <strong>aptitude à prendre les bonnes décisions</strong> : exécution
          de tâches, prises de rendez-vous, délégation, contrôle, refus ou
          acceptation de projets...
        </p>
        <p>
          En d'autres termes, vous serez observé sur votre façon de{" "}
          <strong>gérer les priorités</strong>, de{" "}
          <strong>planifier les tâches</strong> et de les{" "}
          <strong>déléguer</strong>.
        </p>
      </div>
    ),
  },
  {
    icon: <BookOpen className="h-12 w-12 text-primary" />,
    title: "Contexte",
    content: (variablesData) => (
      <div className="space-y-3 text-left">
        <p>
          Vous avez été nommé <strong>responsable par intérim</strong> en
          remplacement de{" "}
          <strong>
            {variablesData.genre_masc_complet}{" "}
            {variablesData.responsable_grand_est_precedent_nom}
          </strong>{" "}
          et vous vous trouvez confronté à la résolution de différentes
          situations — problèmes.
        </p>
        <p>
          Pour mener à bien votre mission, vous disposez d'
          <strong>
            informations sur la société {variablesData.societe_mere_nom}
          </strong>
          , son organisation et les personnes qui composent votre environnement
          professionnel (collaborateurs).
        </p>
        <p>
          Ces informations sont accessibles directement depuis la{" "}
          <strong>barre latérale gauche</strong> de l'application.
        </p>
      </div>
    ),
  },
  {
    icon: <FileText className="h-12 w-12 text-primary" />,
    title: "Ressources à votre disposition",
    content: (variablesData) => (
      <div className="space-y-3">
        <p className="text-left">
          Pour réaliser <strong>InBasket</strong>, vous aurez accès aux
          ressources suivantes depuis la barre latérale gauche :
        </p>
        <ul className="space-y-2 text-left">
          <li className="flex items-start gap-2">
            <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
            <span>
              <strong>L'organigramme</strong> de la société{" "}
              {variablesData.societe_mere_nom} — pour connaître les
              collaborateurs et leurs rôles
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
            <span>
              <strong>Les informations entreprise</strong> — contexte général,
              activité et organisation
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
            <span>
              <strong>Le budget annuel</strong> — données financières de
              l'exercice en cours
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
            <span>
              <strong>L'agenda</strong> — le calendrier de votre prédécesseur
            </span>
          </li>
        </ul>
      </div>
    ),
  },
  {
    icon: <Mail className="h-12 w-12 text-primary" />,
    title: "Les mails",
    content: () => (
      <div className="space-y-3 text-left">
        <p>
          Les mails sont classés par <strong>date de réception</strong>. Vous
          disposez ainsi des mails de votre prédécesseur et de ceux qui vous
          sont adressés directement.
        </p>
        <p>
          Certains mails nécessiteront une <strong>réponse obligatoire.</strong>
          {`  Pour ces mails, nous vous indiquerons lequel des deux formats de réponse suivants est attendu :`}
        </p>
        <p className="pt-2">
          <strong>1. Format « Expliquer vos choix » :</strong>
        </p>
        <p>
          Ce format attend une liste d’actions. Pour chaque action importante
          que vous décidez de prendre (ou de ne pas prendre), expliquez
          clairement pourquoi vous faites ce choix. Quel est votre raisonnement
          ? Quels facteurs avez-vous pris en compte ?
        </p>

        <p className="pt-2">
          <strong>2. Format « Répondre au mail » :</strong>
        </p>
        <p>
          Ce format attend un email de réponse direct au mail reçu, comme vous
          le feriez en situation réelle.
        </p>
        <p>
          L'objectif principal n'est pas de trouver une "bonne" réponse unique —
          car il en existe rarement — mais de nous donner un aperçu de{" "}
          <strong>votre logique d'action</strong>, votre processus de réflexion,
          vos priorités et votre style de management.
        </p>
        <p>
          Soyez vous-même et expliquez le raisonnement qui sous-tend vos
          actions. C'est <strong>votre processus de décision</strong> qui nous
          intéresse avant tout.
        </p>
      </div>
    ),
  },
  {
    icon: <Palette className="h-12 w-12 text-primary" />,
    title: "Les couleurs et étiquettes",
    content: () => (
      <div className="space-y-3 text-left">
        <p>
          Afin d'organiser les mails, des{" "}
          <strong>couleurs sont à votre disposition</strong> et permettent de
          classer les messages par thématique.
        </p>
        <p>
          Sur chaque mail, vous pouvez attribuer une couleur depuis le panneau
          de lecture. Cette fonctionnalité est <strong>facultative</strong> mais
          peut vous aider à structurer votre traitement des mails et à
          visualiser rapidement vos priorités.
        </p>
        <p>
          Vous pouvez également marquer les mails les plus importants avec
          l'icône <Star className="inline h-4 w-4 text-yellow-500" /> pour les
          retrouver facilement via la section <strong>Importants</strong> de la
          barre latérale.
        </p>
      </div>
    ),
  },
  {
    icon: <CheckSquare className="h-12 w-12 text-primary" />,
    title: "Tâches à réaliser",
    content: () => (
      <div className="space-y-4">
        <ul className="space-y-3 text-left">
          <li className="flex items-start gap-3">
            <CheckSquare className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
            <span>Prendre connaissance de tous les mails et documents</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckSquare className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
            <span>Classer les mails par couleur / thème (facultatif)</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckSquare className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
            <span>Répondre aux mails qui nécessitent une réponse</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckSquare className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
            <span>
              Signaler les mails qui vous semblent les plus importants
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckSquare className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
            <span>
              Répondre au dernier mail que vous recevrez lors de cette mise en
              situation
            </span>
          </li>
        </ul>
        <p className="text-sm italic text-muted-foreground">
          Gérez votre temps de manière autonome et traitez les mails dans
          l'ordre qui vous semble le plus pertinent.
        </p>
      </div>
    ),
  },
  {
    icon: <HelpCircle className="h-12 w-12 text-primary" />,
    title: "Accès au tutoriel",
    content: () => (
      <div className="space-y-3 text-center">
        <p>
          Vous pouvez réouvrir ce tutoriel à tout moment en cliquant sur le
          bouton <strong>"Relancer le tutoriel"</strong> situé en bas de la
          barre latérale gauche.
        </p>
        <div className="mx-auto flex w-fit items-center gap-2 rounded-lg border border-sidebar-border bg-sidebar px-4 py-2 text-sm text-sidebar-foreground">
          <HelpCircle className="h-4 w-4" />
          <span>Relancer le tutoriel</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Bonne chance pour votre évaluation !
        </p>
      </div>
    ),
  },
];

interface TutorialModalProps {
  variablesData: VariablesData;
  onClose: () => void;
}

export function TutorialModal({ variablesData, onClose }: TutorialModalProps) {
  const [[slideIndex, _direction], setSlide] = useState<[number, number]>([
    0, 0,
  ]);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback((newIndex: number, newDirection: number) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setSlide([newIndex, newDirection]);
      setIsTransitioning(false);
    }, 150);
  }, []);

  const slide = slides[slideIndex]!;
  const isFirst = slideIndex === 0;
  const isLast = slideIndex === slides.length - 1;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div
        className="relative flex flex-col w-full max-w-2xl max-h-[90vh] rounded-xl bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <span className="text-sm font-medium text-muted-foreground">
            {slideIndex + 1} / {slides.length}
          </span>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 hover:bg-muted transition-colors"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Slide content */}
        <div
          className={cn(
            "flex-1 overflow-y-auto px-6 py-8 transition-opacity duration-150",
            isTransitioning ? "opacity-0" : "opacity-100",
          )}
        >
          {/* Illustration */}
          <div className="mb-6 flex justify-center">
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-primary/10">
              {slide.icon}
            </div>
          </div>

          {/* Title */}
          <h2 className="mb-4 text-center text-xl font-semibold text-foreground">
            {slide.title}
          </h2>

          {/* Content */}
          <div className="min-h-[180px] text-sm leading-relaxed text-foreground/80">
            {slide.content(variablesData)}
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 pb-2">
          {slides.map((_, i) => (
            <div
              key={i}
              className={cn(
                "h-2 w-2 rounded-full transition-colors duration-200",
                i === slideIndex ? "bg-primary" : "bg-muted-foreground/30",
              )}
            />
          ))}
        </div>

        {/* Footer navigation */}
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <Button
            variant="ghost"
            onClick={() => goToSlide(slideIndex - 1, -1)}
            disabled={isFirst || isTransitioning}
            className={cn("gap-2", isFirst && "invisible")}
          >
            <ArrowLeft className="h-4 w-4" />
            Précédent
          </Button>

          {isLast ? (
            <Button onClick={onClose} className="gap-2">
              Commencer
            </Button>
          ) : (
            <Button
              onClick={() => goToSlide(slideIndex + 1, 1)}
              disabled={isTransitioning}
              className="gap-2"
            >
              Suivant
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
